import {useCallback, useEffect, useState} from 'react';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import type {
	usePersonsWithApprovableTimeQuery$data,
	usePersonsWithApprovableTimeQuery$variables,
} from './__generated__/usePersonsWithApprovableTimeQuery.graphql';

const query = graphql`
	query usePersonsWithApprovableTimeQuery($weekStart: String!, $weekEnd: String!) {
		viewer {
			id
			company {
				id
				personsWithApprovableTime(startDate: $weekStart, endDate: $weekEnd)
			}
		}
	}
`;

type PersonIds = ReadonlyArray<string | null> | null;

/**
 * The people whose week holds time that needs approval, for the week being viewed: an owned person
 * with no visible time is a 0h week only when they are not in this list.
 *
 * Null when `enabled` is false (nothing reads it, so it is not fetched), until the viewed week's
 * answer arrives, and when the read fails. Null leaves the page's 0h rows as they were, so a
 * missing answer never removes a row.
 *
 * An answer is used only for the week it was asked for. Moving to another week drops the previous
 * week's answer at once, and a response that lands after the viewer has moved on is ignored, so
 * the list is never applied to another week's rows. Approving or declining rows does not change
 * which people have time needing approval, so the answer is not refetched after a mutation.
 *
 * `refetch` asks again for the week being viewed, for when time changes elsewhere (a socket event).
 * The current answer stays in place until the new one arrives. It is a stable function, so it can
 * be captured by a handler registered once.
 */
export const usePersonsWithApprovableTime = (
	weekStart: string,
	weekEnd: string,
	enabled: boolean
): {personIds: PersonIds; refetch: () => void} => {
	const {fetch} = useForecastFetchQuery<usePersonsWithApprovableTimeQuery$data, usePersonsWithApprovableTimeQuery$variables>(
		query
	);
	const [answer, setAnswer] = useState<{weekStart: string; personIds: PersonIds} | null>(null);
	const [refetchCount, setRefetchCount] = useState(0);
	const refetch = useCallback(() => setRefetchCount(count => count + 1), []);

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}
		let isLatest = true;
		fetch({weekStart, weekEnd}, data => {
			if (isLatest) {
				setAnswer({weekStart, personIds: data?.viewer?.company?.personsWithApprovableTime ?? null});
			}
		});
		return () => {
			isLatest = false;
		};
	}, [weekStart, weekEnd, enabled, refetchCount]);

	return {personIds: enabled && answer?.weekStart === weekStart ? answer.personIds : null, refetch};
};
